<script setup lang="ts">
import { ref, onMounted } from "vue";
import { useRoute } from "vue-router";
import axios from "axios";

const route = useRoute();
const token = route.params.token as string;
const sentiment = route.query.sentiment as string;
const preview = route.query.preview as string;

const api = axios.create({
  baseURL: process.env.VUE_APP_API_ENDPOINT + "/api/v1",
  headers: { "Content-Type": "application/json" },
});

type State =
  | "loading"
  | "positive-redirect"
  | "negative-form"
  | "negative-done"
  | "error";

const state = ref<State>("loading");
const partnerName = ref("");
const customerName = ref("");
const googleReviewUrl = ref<string | null>(null);
const partnerPhone = ref<string | null>(null);
const feedbackText = ref("");
const submitting = ref(false);
const errorMessage = ref("");

onMounted(async () => {
  if (preview) {
    partnerName.value = "Smalt Installations GmbH";
    customerName.value = "Max Mustermann";
    googleReviewUrl.value = "https://google.com";
    partnerPhone.value = "+49 30 12345678";
    state.value = preview as State;
    return;
  }

  try {
    const { data } = await api.get(`/reviews/respond/${token}`);
    partnerName.value = data.partner_name;
    customerName.value = data.customer_name;
    googleReviewUrl.value = data.google_review_url;
    partnerPhone.value = data.partner_phone ?? null;

    if (sentiment === "positive") {
      await api.post(`/reviews/respond/${token}`, { sentiment: "positive" });
      state.value = "positive-redirect";
      if (googleReviewUrl.value) {
        setTimeout(() => {
          window.location.href = googleReviewUrl.value!;
        }, 2000);
      }
    } else if (sentiment === "negative") {
      state.value = "negative-form";
    } else {
      state.value = "error";
      errorMessage.value = "Ungültiger Link.";
    }
  } catch {
    state.value = "error";
    errorMessage.value = "Dieser Link ist leider nicht mehr gültig.";
  }
});

async function submitNegativeFeedback() {
  if (preview) {
    state.value = "negative-done";
    return;
  }
  submitting.value = true;
  try {
    await api.post(`/reviews/respond/${token}`, {
      sentiment: "negative",
      feedback_text: feedbackText.value || null,
    });
    state.value = "negative-done";
  } catch {
    errorMessage.value = "Fehler beim Senden. Bitte versuchen Sie es erneut.";
  } finally {
    submitting.value = false;
  }
}
</script>

<template>
  <div
    class="min-h-screen bg-[#F1F0EE] flex flex-col items-center justify-center px-4 py-12"
  >
    <div class="w-full max-w-md bg-white rounded-2xl shadow-sm p-8 text-center">
      <img
        src="@/assets/smalt-logo-login.svg"
        alt="Smalt"
        class="h-8 mx-auto mb-8"
      />

      <!-- Loading -->
      <div v-if="state === 'loading'" class="text-gray-500 text-sm">
        Einen Moment...
      </div>

      <!-- Positive: redirecting to Google -->
      <div v-else-if="state === 'positive-redirect'">
        <div class="text-4xl mb-4">🙌</div>
        <h1 class="text-xl font-semibold text-gray-900 mb-2">Vielen Dank!</h1>
        <p class="text-gray-500 text-sm mb-6">
          Wir freuen uns über Ihr positives Feedback. Hinterlassen Sie uns gerne
          eine Bewertung auf Google.
        </p>
        <a
          v-if="googleReviewUrl"
          :href="googleReviewUrl"
          target="_blank"
          class="inline-block w-full bg-gray-900 text-white rounded-xl py-3 text-sm font-medium hover:bg-gray-700 transition"
        >
          Jetzt auf Google bewerten
        </a>
        <p v-else class="text-gray-400 text-xs mt-4">
          Kein Google-Bewertungslink hinterlegt.
        </p>
      </div>

      <!-- Negative: feedback form -->
      <div v-else-if="state === 'negative-form'">
        <div class="text-4xl mb-4">😔</div>
        <h1 class="text-xl font-semibold text-gray-900 mb-2">
          Das tut uns leid, {{ customerName || "Kunde" }}.
        </h1>
        <p class="text-gray-500 text-sm mb-6">
          Was hätten wir besser machen können? Ihr Feedback hilft uns, uns zu
          verbessern.
        </p>
        <textarea
          v-model="feedbackText"
          rows="4"
          placeholder="Ihr Feedback (optional)..."
          class="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 resize-none focus:outline-none focus:ring-2 focus:ring-gray-300 mb-4"
        />
        <p v-if="errorMessage" class="text-red-500 text-xs mb-3">
          {{ errorMessage }}
        </p>
        <button
          @click="submitNegativeFeedback"
          :disabled="submitting"
          class="w-full bg-gray-900 text-white rounded-xl py-3 text-sm font-medium hover:bg-gray-700 transition disabled:opacity-50"
        >
          {{ submitting ? "Wird gesendet..." : "Feedback absenden" }}
        </button>
      </div>

      <!-- Negative: thank you -->
      <div v-else-if="state === 'negative-done'">
        <div
          class="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-6"
        >
          <svg
            class="w-8 h-8 text-green-500"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>
        <h1 class="text-xl font-semibold text-gray-900 mb-3">
          Vielen Dank für Ihr Feedback
        </h1>
        <p class="text-gray-500 text-sm leading-relaxed">
          Wir haben Ihre Rückmeldung erhalten und werden uns schnellstmöglich
          darum kümmern.
        </p>
        <div v-if="partnerPhone" class="mt-6 pt-6 border-t border-gray-100">
          <p class="text-xs text-gray-400">
            Bei dringenden Anliegen erreichen Sie uns unter
            <a
              :href="`tel:${partnerPhone}`"
              class="font-medium text-gray-600 hover:underline"
              >{{ partnerPhone }}</a
            >
          </p>
        </div>
      </div>

      <!-- Error -->
      <div v-else-if="state === 'error'">
        <div class="text-4xl mb-4">🔗</div>
        <h1 class="text-xl font-semibold text-gray-900 mb-2">Link ungültig</h1>
        <p class="text-gray-500 text-sm">{{ errorMessage }}</p>
      </div>
    </div>
  </div>
</template>
